/**
 * Catalogue des textes du back-office — **source unique** de tout ce qui s'affiche.
 *
 * DECISIONS A-31 : le back-office est **en français uniquement** et pour un écran de bureau
 * (≥ 1280 px). Il n'y a donc pas d'i18next ici — mais il y a un catalogue unique, pour que
 * « aucun texte codé en dur dans un composant » reste vérifiable : un écran qui affiche du texte
 * importe `M`, il n'écrit pas de chaîne littérale dans son JSX.
 *
 * Les entrées paramétrées sont des fonctions : elles évitent les concaténations dispersées et
 * gardent la ponctuation française (espace insécable avant `:` et `;`) au même endroit.
 */
export const M = {
  app: {
    titre: 'Elemora — back-office',
    sousTitre: 'Édition du contenu et de l’équilibrage',
    deconnexion: 'Se déconnecter',
    compte: (email: string): string => `Connecté : ${email}`,
    jetonMemoire:
      'Session en mémoire : elle est perdue au rechargement de la page, et aucun jeton n’est écrit dans le navigateur.',
    deconnexionConfirmee: 'Session fermée.',
  },

  nav: {
    section: 'Contenu',
    cartes: 'Cartes',
    equilibrage: 'Équilibrage',
    niveaux: 'Niveaux',
    publication: 'Publication',
    sectionSuivi: 'Suivi',
    joueurs: 'Joueurs',
    audit: 'Journal d’audit',
  },

  commun: {
    chargement: 'Chargement…',
    chargementListe: 'Chargement de la liste…',
    chargementFiche: 'Chargement de la fiche…',
    reessayer: 'Réessayer',
    annuler: 'Annuler',
    confirmer: 'Confirmer',
    fermer: 'Fermer',
    actualiser: 'Actualiser',
    precedent: 'Précédent',
    suivant: 'Suivant',
    enregistrer: 'Enregistrer dans le brouillon',
    enregistrementEnCours: 'Enregistrement…',
    retourListe: '← Retour à la liste',
    aucunResultat: 'Aucun résultat.',
    oui: 'Oui',
    non: 'Non',
    vide: '—',
    obligatoire: 'obligatoire',
    brouillon: 'Brouillon',
    versionPubliee: 'Version publiée',
    page: (courante: number, total: number): string =>
      `Page ${String(courante)} sur ${String(total)}`,
    compteur: (affiches: number, total: number): string =>
      `${String(affiches)} sur ${String(total)}`,
    total: (total: number): string => `${String(total)} au total`,
    identifiant: 'Identifiant',
    actions: 'Actions',
    modifier: 'Modifier',
    details: 'Détail',
    masquer: 'Masquer',
    chargementImpossible: 'Chargement impossible.',
  },

  avertissements: {
    brouillon:
      'Les modifications vont dans le brouillon : les joueurs ne les voient qu’après une publication.',
    idsImmuables:
      'Les identifiants sont immuables : ils ne sont pas modifiables dans ce formulaire.',
  },

  connexion: {
    titre: 'Connexion au back-office',
    sousTitre:
      'Réservé aux comptes de rôle administrateur. Le jeton de session reste en mémoire du navigateur.',
    email: 'Adresse électronique',
    motDePasse: 'Mot de passe',
    valider: 'Se connecter',
    validationEnCours: 'Connexion…',
    refusNonAdmin:
      'Ce compte n’est pas administrateur : le back-office refuse de l’ouvrir. Utilisez un compte de rôle « admin ».',
    identifiantsInvalides: 'Adresse ou mot de passe incorrect.',
    champsRequis: 'Renseignez l’adresse et le mot de passe.',
  },

  cartes: {
    titre: 'Cartes',
    sousTitre: (total: number): string =>
      `${String(total)} cartes éditables dans le brouillon courant.`,
    recherche: 'Rechercher (identifiant ou nom)',
    filtreRarete: 'Rareté',
    filtreElement: 'Élément',
    toutes: 'Toutes',
    tous: 'Tous',
    colonneId: 'Identifiant',
    colonneNom: 'Nom',
    colonneRarete: 'Rareté',
    colonneElement: 'Élément',
    colonnePoints: 'Points',
    colonneEffets: 'Effets',
    effets: (nombre: number): string =>
      nombre > 1 ? `${String(nombre)} effets` : `${String(nombre)} effet`,
    ouvrir: 'Ouvrir',
    introuvable: (id: string): string => `Carte inconnue : « ${id} » (404 NOT_FOUND).`,
    editionTitre: (id: string): string => `Carte ${id}`,
    nom: 'Nom (clé de contenu)',
    description: 'Description (clé de contenu)',
    rarete: 'Rareté',
    element: 'Élément',
    points: 'Points de collection',
    tags: 'Étiquettes (séparées par des virgules)',
    tagsAide: 'Exemple : feu, brulure, degats',
    cumul: 'Mode de cumul',
    cumulFixe: 'additif (seul mode autorisé en v1)',
    effetsTitre: 'Effets (1 ou 2)',
    effetsAide:
      'Liste fermée de six formes. Un effet inconnu est refusé par le serveur comme par la validation locale.',
    ajouterEffet: 'Ajouter un effet',
    supprimerEffet: 'Supprimer',
    typeEffet: 'Type d’effet',
    enregistree:
      'Carte enregistrée dans le brouillon. Les joueurs ne la verront qu’après publication.',
    aucuneModification: 'Aucune modification à enregistrer.',
    validationLocale: 'La saisie ne respecte pas le contrat : corrigez les champs signalés.',
  },

  effets: {
    type: {
      bonusStat: 'Bonus de statistique',
      bonusEffet: 'Bonus d’effet élémentaire',
      bonusEconomie: 'Bonus d’économie',
      ressource: 'Ressource immédiate',
      pvParcours: 'PV du parcours',
      synergie: 'Synergie conditionnelle',
    },
    stat: 'Statistique',
    statTour: {
      degats: 'Dégâts',
      portee: 'Portée',
      cadenceMs: 'Cadence (ms)',
      coutTour: 'Coût de pose',
      coutAmelioration: 'Coût d’amélioration',
      vitesseProjectile: 'Vitesse du projectile',
    },
    cible: 'Cible',
    economie: {
      eclatsGagnes: 'Éclats gagnés',
      coutTour: 'Coût de pose',
      coutAmelioration: 'Coût d’amélioration',
    },
    ressource: {
      jokers: 'Jokers',
      eclatsRun: 'Éclats de la run',
    },
    champ: 'Champ d’effet',
    champEffet: {
      degats: 'Dégâts',
      duree: 'Durée',
      facteur: 'Facteur',
      cibles: 'Cibles',
    },
    valeur: 'Valeur',
    element: 'Élément',
    elementAucun: 'Tous',
    elementNeutre: 'Neutre',
    archetype: 'Archétype',
    archetypeAucun: 'Tous',
    archetypeNoms: {
      mono: 'Mono-cible',
      zone: 'Zone',
      ligne: 'Ligne',
      chain: 'Chaîne',
      aura: 'Aura',
    },
    conditionElements: 'Éléments de la condition',
    conditionMinTours: 'Tours minimum (facultatif)',
    conditionEffet: 'Effet accordé par la synergie',
  },

  equilibrage: {
    titre: 'Équilibrage',
    sousTitre: 'Paramètres de `balance` — toutes les clés, bornes rappelées sous chaque champ.',
    enregistre: 'Équilibrage enregistré dans le brouillon.',
    colonneCle: 'Clé',
    colonneValeur: 'Valeur',
    colonneBornes: 'Bornes',
    pointsRarete: 'Points par rareté',
    deckDepart: 'Deck de départ (identifiants de cartes)',
    deckDepartAide: 'Un identifiant par ligne ou séparés par des virgules.',
    archetypesTitre: 'Géométrie des archétypes de tours (A-29)',
    archetypesAide:
      'Six paramètres globaux, réglables ici sans redéploiement : rayon de zone, facteur secondaire, chute de ligne, cibles et portée de chaîne, chute de chaîne.',
    bornes: {
      entierPositif: 'entier ≥ 1',
      entierNonNegatif: 'entier ≥ 0',
      probabilite: 'nombre entre 0 et 1',
      facteurUnMax: 'nombre > 0 et ≤ 1',
      superieurAUn: 'nombre > 1',
      positif: 'nombre > 0',
      listeIdentifiants: 'liste non vide d’identifiants',
    },
  },

  niveaux: {
    titre: 'Niveaux',
    sousTitre: (total: number): string =>
      `${String(total)} niveaux éditables (40 niveaux de campagne + niv_illimite).`,
    recherche: 'Rechercher (identifiant ou numéro)',
    filtreMode: 'Mode',
    filtreDa: 'Direction artistique',
    colonneId: 'Identifiant',
    colonneNumero: 'Numéro',
    colonneMode: 'Mode',
    colonneDa: 'DA',
    ouvrir: 'Ouvrir',
    modes: { limite: 'Limité', illimite: 'Illimité' },
    editionTitre: (id: string): string => `Niveau ${id}`,
    introuvable: (id: string): string => `Niveau inconnu : « ${id} » (404 NOT_FOUND).`,
    pvParcours: 'PV du parcours',
    eclatsDepart: 'Éclats de départ (facultatif)',
    eclatsDepartAide: 'Vide = paramètre global `eclats_depart`.',
    vaguesTitre: 'Vagues',
    vague: (index: number): string => `Vague ${String(index + 1)}`,
    boss: 'Vague de boss',
    groupesTitre: 'Groupes d’ennemis',
    ennemiId: 'Identifiant d’ennemi',
    nombre: 'Nombre',
    delaiMs: 'Délai (ms)',
    intervalleMs: 'Intervalle (ms)',
    ajouterVague: 'Ajouter une vague',
    supprimerVague: 'Supprimer la vague',
    ajouterGroupe: 'Ajouter un groupe',
    supprimerGroupe: 'Supprimer le groupe',
    parcoursTitre: 'Parcours et emplacements (lecture seule)',
    parcoursAide:
      'La géométrie est revérifiée par le serveur (segments orthogonaux, grille 32 × 18) : elle n’est pas éditable ici.',
    noeuds: (nombre: number): string => `${String(nombre)} nœuds`,
    emplacements: (nombre: number): string => `${String(nombre)} emplacements`,
    enregistre: 'Niveau enregistré dans le brouillon.',
    referencesAide:
      'Les références croisées (identifiants d’ennemis, de cartes) sont vérifiées à la publication : un identifiant inconnu bloque la publication, pas l’enregistrement du brouillon.',
  },

  publication: {
    titre: 'Publication',
    sousTitre: 'Publier le brouillon remplace la version servie aux joueurs, sans redéploiement.',
    versionPubliee: 'Version publiée en vigueur',
    versionInconnue: 'Version publiée inconnue (manifeste illisible).',
    etatBrouillon: 'Brouillon en attente',
    brouillonOui:
      'Oui — des écritures sont postérieures à la dernière publication (déduit du journal d’audit).',
    brouillonNon: 'Aucune écriture postérieure à la dernière publication.',
    brouillonIndetermine:
      'Indéterminé : aucune route du contrat n’expose l’état du brouillon (constat remonté, cf. livrable).',
    consequencesTitre: 'Ce que la publication déclenche',
    consequences: [
      'Une nouvelle version de contenu est créée et son hash devient la version servie.',
      'Le brouillon est consommé : il ne reste plus rien à publier ensuite.',
      'L’ancienne version est conservée dans l’historique.',
      'Les runs en cours sont closes (§8.5) : le joueur reçoit RUN_VERSION_MISMATCH à sa requête suivante et ne gagne rien sur cette run.',
      'L’opération est tracée dans le journal d’audit (action content.publish).',
    ],
    bouton: 'Publier le brouillon…',
    confirmationTitre: 'Publier le brouillon ?',
    confirmationCorps:
      'Cette action est irréversible pour les parties en cours : elles seront closes. Vérifiez la version avant et après.',
    confirmationValider: 'Publier maintenant',
    confirmationAnnuler: 'Revenir sans publier',
    publieTitre: 'Publication effectuée',
    hashAvant: 'Hash avant publication',
    hashApres: 'Hash après publication',
    hashInchange: 'Identique : le brouillon était revenu à la version publiée, rien n’a changé.',
    publieLe: (date: string): string => `Publié le ${date}`,
    publieLeLabel: 'Publié le',
    aucunBrouillon: 'Rien à publier : il n’y a pas de brouillon en attente.',
    echecValidation:
      'Publication refusée par le serveur : le brouillon complet est invalide. Rien n’a été publié.',
    echecVersion:
      'La version publiée a changé depuis le chargement de l’écran : rechargez avant de republier.',
    gardeOptimiste:
      'La version annoncée au serveur est celle affichée ci-dessus (garde d’optimiste).',
  },

  joueurs: {
    titre: 'Joueurs',
    sousTitre: 'Lecture seule : aucune route d’écriture sur les comptes ni la progression (A-26).',
    lectureSeule: 'Écran de consultation. Aucun formulaire, aucune action de modification.',
    colonnePseudo: 'Pseudo',
    colonneEmail: 'Adresse (masquée)',
    colonneRole: 'Rôle',
    colonneLocale: 'Langue',
    colonneCreation: 'Créé le',
    colonneNiveau: 'Niveau max',
    colonneEclats: 'Éclats',
    colonnePoints: 'Points de collection',
    colonneSupprime: 'Supprimé le',
  },

  audit: {
    titre: 'Journal d’audit',
    sousTitre:
      'Une ligne par écriture et par publication. Adresses et secrets sont masqués par le serveur ; l’affichage les masque une seconde fois.',
    colonneDate: 'Horodatage',
    colonneActeur: 'Acteur',
    colonneAction: 'Action',
    colonneCible: 'Cible',
    actions: {
      'card.update': 'Modification de carte',
      'balance.update': 'Modification de l’équilibrage',
      'level.update': 'Modification de niveau',
      'content.publish': 'Publication de contenu',
    },
    actionInconnue: (action: string): string => action,
    acteurSupprime: 'compte supprimé',
    avant: 'Avant',
    apres: 'Après',
    aucunDetail: 'Aucun détail enregistré.',
  },

  erreurs: {
    reseau: 'API injoignable : vérifiez que le serveur répond, puis réessayez.',
    generique: 'L’appel a échoué.',
    serveur: (message: string): string => `Refus du serveur : ${message}`,
    code: (code: string): string => `Code d’erreur : ${code}`,
    detailsTitre: 'Détail du refus',
    FORBIDDEN: 'Droits insuffisants : ce compte n’est pas administrateur.',
    AUTH_REQUIRED: 'Session expirée : reconnectez-vous.',
    AUTH_INVALID_CREDENTIALS: 'Adresse ou mot de passe incorrect.',
    NOT_FOUND: 'Ressource inconnue (404).',
    VALIDATION_FAILED: 'La saisie a été refusée par la validation du serveur (400).',
    INTERNAL_ERROR: 'Erreur interne du serveur.',
    RATE_LIMITED: 'Trop de requêtes : patientez un instant.',
  },

  introuvable: {
    titre: 'Écran inconnu',
    corps: 'Cette adresse ne correspond à aucun écran du back-office.',
    retour: 'Retour aux cartes',
  },

  elements: {
    feu: 'Feu',
    eau: 'Eau',
    terre: 'Terre',
    air: 'Air',
    neutre: 'Neutre',
  },

  raretes: {
    commune: 'Commune',
    rare: 'Rare',
    legendaire: 'Légendaire',
    mythique: 'Mythique',
  },
} as const;

/** Type du catalogue, pour les composants qui le reçoivent en propriété (tests). */
export type Catalogue = typeof M;

/**
 * Libellé de repli d'un code d'erreur du contrat.
 *
 * La liste `ERROR_CODES` compte 21 codes ; le back-office n'en rencontre qu'une poignée. Plutôt
 * que de dupliquer 21 phrases, on écrit celles qui ont un sens ici et on retombe sur un texte
 * neutre pour les autres — le message du serveur reste affiché au-dessus, c'est lui qui informe.
 */
const TEXTES_ERREURS: Readonly<Record<string, string>> = {
  FORBIDDEN: 'Droits insuffisants : ce compte n’est pas administrateur.',
  AUTH_REQUIRED: 'Session expirée : reconnectez-vous.',
  AUTH_TOKEN_EXPIRED: 'Session expirée : reconnectez-vous.',
  AUTH_INVALID_CREDENTIALS: 'Adresse ou mot de passe incorrect.',
  NOT_FOUND: 'Ressource inconnue (404).',
  VALIDATION_FAILED: 'La saisie a été refusée par la validation du serveur (400).',
  INTERNAL_ERROR: 'Erreur interne du serveur.',
  RATE_LIMITED: 'Trop de requêtes : patientez un instant.',
  NOT_IMPLEMENTED: 'Fonction non implémentée côté serveur (501).',
};

/** Message affichable pour un code d'erreur, jamais vide. */
export function messageErreurCode(code: string): string {
  return TEXTES_ERREURS[code] ?? `L’appel a échoué (${code}).`;
}
